/**
 * Subscription Plan Slide Component
 * Displays the active plan details in a slide
 */
if (!customElements.get('subscription-plan-slide')) {
  customElements.define(
    'subscription-plan-slide',
    class SubscriptionPlanSlide extends HTMLElement {
      constructor() {
        super();
        this.unsubscribe = [];
      }

      connectedCallback() {
        this.cacheElements();
        this.initializeFromActivePlan();
        this.setupEventListeners();
        Utils.HeightManager.init(this, '--slide-plan-content-height');
      }

      cacheElements() {
        this.planSubheading = this.querySelector('[data-plan-subheading]');
        this.imageContainer = this.querySelector('[data-plan-image-placeholder]');
        this.superTitle = this.querySelector('[data-supertitle]');
        this.planPrice = this.querySelector('[data-price]');
      }

      initializeFromActivePlan() {
        if (!window.initialVariantDetails) {
          const initialVariantElement = document.querySelector('subscription-product-slide [data-variant-details]');
          if (initialVariantElement) {
            try {
              this.variantDetails = Utils.parseJSON(initialVariantElement.textContent);
              this.selectedVariant = document.querySelector('subscription-product-slide')?.dataset.selectedVariant;
            } catch (error) {
              console.error('Error parsing variant details:', error);
            }
          }
        } else {
          this.variantDetails = window.initialVariantDetails;
          this.selectedVariant = document.querySelector('subscription-product-slide')?.dataset.selectedVariant;
        }

        const activePlanElement = document.querySelector('subscription-plan [data-plan-toggle].tw-is-active');
        if (activePlanElement) {
          const planElement = activePlanElement.closest('subscription-plan');
          if (planElement) {
            try {
              const planDetailsElement = planElement.querySelector('[data-plan-details]');
              if (planDetailsElement) {
                const planDetails = Utils.parseJSON(planDetailsElement.textContent);
                const planImage = planElement.querySelector('[data-plan-img]');

                this.selectedPlan = {
                  details: planDetails,
                  image: planImage,
                };

                this.updatePlanSubheading();
                this.updatePlanImage();
                this.updateStepState();
                this.updateDiscountValues();
              }
            } catch (error) {
              console.error('Error initializing from active plan:', error);
            }
          }
        }
      }

      setupEventListeners() {
        this.unsubscribe.push(
          subscribe('subscription-flow:state-update', (data) => {
            if (data.step == this.dataset.step) {
              const plans = this.querySelectorAll('subscription-plan');

              plans.forEach((plan) => {
                const firstChild = plan.firstElementChild;

                if (firstChild.classList.contains('tw-is-active')) {
                  const price = plan.querySelector('[data-price]');
                  const button_price = document.querySelectorAll('.button__price');

                  button_price.forEach((elem) => {
                    elem.innerText = `(${price.dataset.priceValue})`;
                  });
                }
              });
            }
            if (data.variantDetails && (!this.variantDetails || data.selectedVariant !== this.selectedVariant)) {
              this.variantDetails = data.variantDetails;
              this.selectedVariant = data.selectedVariant;
              this.updateStepState();
              this.updateDiscountValues();
            }

            if (
              data.selectedPlan &&
              data.planDetails &&
              (!this.selectedPlan || JSON.stringify(data.planDetails) !== JSON.stringify(this.selectedPlan?.details))
            ) {
              this.selectedPlan = data.selectedPlan;
              this.updatePlanSubheading();
              this.updatePlanImage();
            }
          })
        );

        Utils.setupCommonSlideEventListeners(this);
      }

      updateStepState() {
        if (!this.selectedVariant) return;
        const elements = this.querySelectorAll('[data-variant-id]');
        elements.forEach((element) => {
          const isActive = element.dataset.variantId === this.selectedVariant;
          element.classList.toggle('tw-is-active', isActive);
        });
      }

      updateDiscountValues() {
        if (!this.variantDetails || !this.variantDetails.discount_percentage) return;

        if (this.superTitle) {
          const spanElement = this.superTitle.querySelector('span');
          if (spanElement) {
            spanElement.textContent = this.variantDetails.discount_percentage + '%';
          }
        }

        if (this.selectedPlan && this.planSubheading) {
          const spanElement = this.planSubheading.querySelector('span');
          if (spanElement) {
            spanElement.textContent = `${this.variantDetails.discount_percentage}%`;
          }
        }
      }

      updatePlanSubheading() {
        if (!this.selectedPlan?.details || !this.planSubheading) return;

        if (this.selectedPlan.details.main_description) {
          this.planSubheading.innerHTML = this.selectedPlan.details.main_description;
          this.updateDiscountValues();
        }
      }

      updatePlanImage() {
        if (!this.selectedPlan || !this.imageContainer) return;

        const previousPlanImage = this.imageContainer.querySelector('[data-plan-img]');
        if (previousPlanImage) {
          previousPlanImage.remove();
        }

        if (this.selectedPlan.image) {
          this.imageContainer.appendChild(this.selectedPlan.image.cloneNode(true));
          const newImage = this.imageContainer.querySelector('[data-plan-img]');
          if (newImage) {
            newImage.classList.add('tw-is-active');
            newImage.classList.remove('tw-hidden');
          }
        }
      }

      disconnectedCallback() {
        Utils.HeightManager.disconnect(this);

        this.unsubscribe.forEach((unsub) => {
          if (typeof unsub === 'function') unsub();
        });
      }
    }
  );
}

/**
 * Subscription Plan Component
 * Manages plan selection and pricing calculation
 */
if (!customElements.get('subscription-plan')) {
  customElements.define(
    'subscription-plan',
    class SubscriptionPlan extends HTMLElement {
      constructor() {
        super();
      }

      connectedCallback() {
        this.planElement = this.querySelector('[data-plan-toggle]');
        this.planDetails = Utils.parseJSON(this.querySelector('[data-plan-details]').textContent);
        this.planImage = this.querySelector('[data-plan-img]');
        this.planPrice = this.querySelector('[data-price]');
        this.planAccordion = this.querySelector('accordion-item');

        if (!this.variantDetails) {
          const initialVariantElement = document.querySelector('subscription-product-slide [data-variant-details]');
          if (initialVariantElement) {
            try {
              this.variantDetails = Utils.parseJSON(initialVariantElement.textContent);
              this.selectedVariant = document.querySelector('subscription-product-slide')?.dataset.selectedVariant;

              this.updatePlanPrice();
              this.updatePlanDescription();
            } catch (error) {
              console.error('Error parsing initial variant details:', error);
            }
          }
        }

        if (this.planElement.classList.contains('tw-is-active')) {
          publish('subscription-flow:selected-plan', {
            details: this.planDetails,
            image: this.planImage,
            element: this,
          });

          setTimeout(() => this._openAccordion(), 0);
        }

        this.planElement.addEventListener('click', () => {
          const wasActive = this.planElement.classList.contains('tw-is-active');
          this.updatePlanState();

          if (!wasActive) {
            publish('subscription-flow:selected-plan', {
              details: this.planDetails,
              image: this.planImage,
              element: this,
            });
          }
        });

        this.unsubscribeVariant = subscribe('subscription-flow:selected-variant', (data) => {
          this.variantDetails = data.variant_details;
          this.selectedVariant = data.selected_variant;
          this.updatePlanPrice();
          this.updatePlanDescription();
        });

        this.unsubscribePlan = subscribe('subscription-flow:selected-plan', (data) => {
          if (data.element === this) {
            this._openAccordion();
          } else {
            this._closeAccordion();
          }
        });

        this.unsubscribeSlideVisible = subscribe('subscription-flow:state-update', (data) => {
          if (String(data.step) === this.closest('[data-step]')?.dataset.step) {
            if (this.planAccordion?.hasAttribute('open')) {
              this.planAccordion.recalculateHeight();
            }
            this.unsubscribeSlideVisible();
          }
        });
      }

      updatePlanState() {
        if (!this.planElement.classList.contains('tw-is-active')) {
          this.planElement.classList.add('tw-is-active');

          const allPlanElements = this.parentElement.querySelectorAll('[data-plan-toggle]');
          allPlanElements.forEach((element) => {
            if (element !== this.planElement) {
              element.classList.remove('tw-is-active');
            }
          });

          this.updatePlanPrice();
        }
      }

      updatePlanDescription() {
        const dynamicDetailsElement = this.querySelector('[data-dynamic-description]');
        if (!this.variantDetails || !dynamicDetailsElement) return;

        const formatPrice = (price) => {
          if (!price) return '';
          return Shopify.formatMoney(price, window.moneyFormat).replace(/"/g, '');
        };

        if (!this._detailsHtml) this._detailsHtml = dynamicDetailsElement.innerHTML;

        let detailsHtml = this._detailsHtml;
        const isDefaultPlan = this.planDetails.plan_variant === 'default';
        const caseComparePrice = formatPrice(this.variantDetails.compare_at_price);
        const casePrice = formatPrice(
          isDefaultPlan
            ? this.variantDetails.price
            : this.variantDetails[this.planDetails.plan_variant]?.price ?? this.variantDetails.one_off_2?.price
        );
        const refillComparePrice = formatPrice(
          !isDefaultPlan || !this.planDetails.refill_discount_price ? '' : this.planDetails.refill_price
        );
        const refillPrice = formatPrice(
          isDefaultPlan && this.planDetails.refill_discount_price
            ? this.planDetails.refill_discount_price
            : this.planDetails.refill_price * this.planDetails.refill_count
        );
        const refillRecurringPrice = formatPrice(
          (isDefaultPlan && this.planDetails.refill_discount_price
            ? this.planDetails.refill_discount_price
            : this.planDetails.refill_price * this.planDetails.refill_count) * 3
        );

        const refillIndividualPrice = formatPrice(
          isDefaultPlan && this.planDetails.refill_discount_price
            ? this.planDetails.refill_discount_price
            : this.planDetails.refill_price
        );

        const subscriptionDiscount = isDefaultPlan
          ? this.planDetails.subscription_data?.price_adjustments[0]?.value ?? ''
          : '';

        const nextOrderDate = (subscriptionInterval = 1) => {
          const date = new Date();
          date.setHours(0, 0, 0, 0);
          date.setMonth(date.getMonth() + subscriptionInterval);

          const locale = window.Shopify.locale;
          const country = window.Shopify.country;

          return new Intl.DateTimeFormat(locale && country ? `${locale}-${country}` : navigator.language, {
            dateStyle: 'long',
          }).format(date);
        };

        detailsHtml = detailsHtml.replaceAll(
          '{casePrice}',
          `${caseComparePrice ? `<s>${caseComparePrice}</s>` : ''} <span>${casePrice}</span>`.trim()
        );

        detailsHtml = detailsHtml.replaceAll(
          '{refillPrice}',
          `${refillComparePrice ? `<s>${refillComparePrice}</s>` : ''} <span>${refillPrice}</span>`.trim()
        );

        detailsHtml = detailsHtml.replaceAll('{refillRecurringPrice}', `<span>${refillRecurringPrice}</span>`.trim());
        detailsHtml = detailsHtml.replaceAll('{refillIndividualPrice}', `<span>${refillIndividualPrice}</span>`.trim());
        detailsHtml = detailsHtml.replaceAll('{subscriptionDiscount}', `${subscriptionDiscount}%`);
        detailsHtml = detailsHtml.replaceAll('{nextProcessDate}', `${nextOrderDate()}`);

        dynamicDetailsElement.innerHTML = detailsHtml;
      }

      updatePlanPrice(returnPrice = false) {
        if (!this.planPrice || !this.variantDetails || !this.planDetails) {
          console.error('Error: Missing elements required for price calculation');
          return;
        }

        const refillCount = parseInt(this.planDetails.refill_count || 1);
        const refillPriceInCents = parseInt(this.planDetails.refill_price || 0);
        const isPlanDefault = this.planDetails.plan_variant === 'default';
        const isPlanOneOff = this.planDetails.plan_variant === 'one_off';
        const isPlanOneOffTwo = this.planDetails.plan_variant === 'one_off_2';
        const discountPercentage = parseInt(this.variantDetails.discount_percentage || 0);
        const refillDiscountedPrice = parseInt(this.planDetails.refill_discount_price || 0);
        const compareAtPrice =
          !isPlanOneOff && !isPlanOneOffTwo ? parseFloat(this.variantDetails?.data_compare_at_price || 0) : 0;
        const currency = this.closest('subscription-flow').dataset.currency;
        const sellingPlanData = this.planDetails.subscription_data;

        let casePriceInCents = 0;

        if (isPlanOneOffTwo && this.variantDetails.one_off_2?.price) {
          casePriceInCents = parseInt(this.variantDetails.one_off_2.price || 0);
        } else if (isPlanOneOffTwo && this.variantDetails.one_off?.price) {
          casePriceInCents = parseInt(this.variantDetails.one_off.price || 0);
        } else if (isPlanOneOff && this.variantDetails.one_off?.price) {
          casePriceInCents = parseInt(this.variantDetails.one_off.price || 0);
        } else {
          casePriceInCents = parseInt(this.variantDetails.price || 0);
        }

        let priceInCents;

        if (isPlanDefault && refillDiscountedPrice > 0) {
          // priceInCents = refillDiscountedPrice + casePriceInCents;
          const refillsDiscountAsPercentage =
            sellingPlanData?.price_adjustments.length &&
            sellingPlanData.price_adjustments[0].value_type === 'percentage'
              ? parseFloat(sellingPlanData.price_adjustments[0].value)
              : 0;
          priceInCents = this.calculatePrice({
            refillCount,
            refillPriceInCents,
            casePriceInCents,
            refillsDiscountAsPercentage: refillsDiscountAsPercentage,
          });
        } else {
          priceInCents = this.calculatePrice({
            refillCount,
            refillPriceInCents,
            casePriceInCents,
            refillsDiscountAsPercentage: 0,
          });
        }

        const formattedPrice = Shopify.formatMoney(priceInCents, `${currency}{{amount}}`);

        if (returnPrice) {
          return formattedPrice;
        }

        this.planPrice.innerHTML = formattedPrice;
        this.planPrice.dataset.priceValue = formattedPrice;

        if (compareAtPrice) {
          const compareAtPriceInCents = compareAtPrice * 100;
          const formattedCompareAtPrice = Shopify.formatMoney(compareAtPriceInCents, `${currency}{{amount}}`);

          const compareAtPriceSpan = document.createElement('span');
          compareAtPriceSpan.textContent = formattedCompareAtPrice;
          this.planPrice.appendChild(compareAtPriceSpan);
        } else if (isPlanDefault && discountPercentage > 0) {
          const compareAtPriceInCents = this.calculateCompareAtPrice(priceInCents, discountPercentage);
          const formattedCompareAtPrice = Shopify.formatMoney(compareAtPriceInCents, `${currency}{{amount}}`);

          const compareAtPriceSpan = document.createElement('span');
          compareAtPriceSpan.textContent = formattedCompareAtPrice;
          this.planPrice.appendChild(compareAtPriceSpan);
        }
      }

      calculatePrice({ refillCount, refillPriceInCents, casePriceInCents, refillsDiscountAsPercentage = 0 }) {
        const adjustmentMultiplier = 1 - refillsDiscountAsPercentage / 100;
        const refillsSubTotal = refillPriceInCents * refillCount * adjustmentMultiplier;
        const totalPriceInCents = refillsSubTotal + (casePriceInCents || 0);

        return Math.round(totalPriceInCents);
      }

      calculateCompareAtPrice(priceInCents, discountPercentage) {
        if (!discountPercentage || discountPercentage <= 0) {
          return priceInCents;
        }

        const discountMultiplier = 1 - discountPercentage / 100;
        return Math.round(priceInCents / discountMultiplier);
      }

      _openAccordion() {
        if (!this.planAccordion) return;
        this.planAccordion.setAttribute('open', '');
        this.planAccordion._openContent();
      }

      _closeAccordion() {
        if (!this.planAccordion) return;
        this.planAccordion.removeAttribute('open');
        this.planAccordion._closeContent();
      }

      disconnectedCallback() {
        if (this.unsubscribeVariant) this.unsubscribeVariant();
        if (this.unsubscribePlan) this.unsubscribePlan();
        if (this.unsubscribeSlideVisible) this.unsubscribeSlideVisible();
      }
    }
  );
}
